// src/admin/AdminSiteEffectsPage.js (Ported to Versionnet structure)
import React, { useState, useEffect } from 'react';
import AdminLayout from './AdminLayout'; // Adjusted import path for Versionnet
import pb from '../lib/pocketbase';
import { FaSnowflake, FaCloudRain, FaLeaf, FaMagic } from 'react-icons/fa';

import { useEffectsData } from '../context/EffectsContext';

function AdminSiteEffectsPage() {
    const { effects: contextEffects, loading: contextLoading, refresh } = useEffectsData();
    const [loading, setLoading] = useState(false); // Local loading for actions
    const [effects, setEffects] = useState(contextEffects || []);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');

    useEffect(() => {
        if (contextEffects) {
            setEffects(contextEffects);
        }
    }, [contextEffects]);


    const loadEffects = () => {
        refresh();
    };


    const handleCreateDefault = async () => {
        setLoading(true);
        setMessage('');
        setError('');
        try {
            // Créer les effets par défaut
            await pb.collection('site_seasonal_effects').create({
                name: 'Neige Hivernale',
                type: 'snow',
                is_active: false,
                intensity: 50
            });
            await pb.collection('site_seasonal_effects').create({
                name: 'Pluie d\'Automne',
                type: 'rain',
                is_active: false,
                intensity: 100
            });
            await pb.collection('site_seasonal_effects').create({
                name: 'Feuilles d\'Automne',
                type: 'leaves',
                is_active: false,
                intensity: 30
            });
            setMessage("Effets par défaut créés avec succès !");
            loadEffects();
        } catch (err) {
            setError("Erreur lors de la création : " + err.message);
            setLoading(false);
        }
    };

    const toggleEffect = async (effect) => {
        try {
            // Désactiver tous les autres si on active celui-ci
            const isActivating = !effect.is_active;

            if (isActivating) {
                // On doit d'abord tout désactiver pour n'en avoir qu'un seul actif (optionnel, mais mieux pour éviter surcharge)
                // Note: faire ça en boucle n'est pas atomique mais suffisant ici
                for (let e of effects) {
                    if (e.is_active && e.id !== effect.id) {
                        await pb.collection('site_seasonal_effects').update(e.id, { is_active: false });
                    }
                }
            }

            await pb.collection('site_seasonal_effects').update(effect.id, { is_active: isActivating });
            loadEffects();
        } catch (err) {
            alert("Erreur: " + err.message);
        }
    };

    const iconMap = {
        'snow': <FaSnowflake />,
        'rain': <FaCloudRain />,
        'leaves': <FaLeaf />,
        'default': <FaMagic />
    };

    return (
        <div className="section">
            <div className="section-inner">
                <div className="section-header">
                    <div className="section-header-left">
                        <h2>Gestion des effets de saison</h2>
                        <p className="text-muted">Activez des effets visuels sur tout le site (neige, pluie, etc.) pour des événements spéciaux.</p>
                    </div>
                </div>

                {error && <div className="alert alert-danger">{error}</div>}
                {message && <div className="alert alert-success">{message}</div>}

                {loading ? (
                    <div className="loading-state">Chargement des effets...</div>
                ) : (
                    <>
                        {effects.length === 0 && !error && (
                            <div className="empty-state">
                                <p>Aucun effet configuré.</p>
                                <button className="btn btn-primary" onClick={handleCreateDefault}>
                                    Initialiser les effets par défaut
                                </button>
                            </div>
                        )}

                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1.5rem', marginTop: '1rem' }}>
                            {effects.map(effect => (
                                <div key={effect.id} className="card" style={{
                                    borderLeft: effect.is_active ? '4px solid var(--primary)' : '4px solid var(--border-subtle)',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    position: 'relative',
                                    overflow: 'hidden'
                                }}>
                                    {/* Card Header */}
                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        marginBottom: '1rem',
                                        borderBottom: '1px solid var(--border-subtle)',
                                        paddingBottom: '0.75rem'
                                    }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                            <div style={{
                                                fontSize: '1.5rem',
                                                color: effect.is_active ? 'var(--primary)' : 'var(--text-muted)',
                                                display: 'flex'
                                            }}>
                                                {iconMap[effect.type] || iconMap['default']}
                                            </div>
                                            <h3 style={{ margin: 0, fontSize: '1.1rem', color: 'var(--text-main)' }}>{effect.name}</h3>
                                        </div>
                                        <div className={`status-badge ${effect.is_active ? 'active' : ''}`} style={{
                                            fontSize: '0.75rem',
                                            padding: '0.25rem 0.5rem',
                                            borderRadius: '4px',
                                            backgroundColor: effect.is_active ? 'rgba(16, 185, 129, 0.1)' : 'var(--bg-subtle)',
                                            color: effect.is_active ? '#10b981' : 'var(--text-muted)',
                                            fontWeight: '600',
                                            border: '1px solid transparent',
                                            borderColor: effect.is_active ? 'rgba(16, 185, 129, 0.2)' : 'var(--border-subtle)'
                                        }}>
                                            {effect.is_active ? 'ACTIF' : 'INACTIF'}
                                        </div>
                                    </div>

                                    {/* Card Body */}
                                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1.5rem' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem', color: 'var(--text-muted)' }}>
                                            <span>Type:</span>
                                            <span style={{ color: 'var(--text-main)', textTransform: 'capitalize' }}>{effect.type}</span>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem', color: 'var(--text-muted)' }}>
                                            <span>Intensité:</span>
                                            <span style={{ color: 'var(--text-main)' }}>{effect.intensity}</span>
                                        </div>
                                    </div>

                                    {/* Card Footer / Actions */}
                                    <div style={{ marginTop: 'auto' }}>
                                        <button
                                            className={`btn w-100 ${effect.is_active ? 'btn-outline' : 'btn-primary'}`}
                                            onClick={() => toggleEffect(effect)}
                                        >
                                            {effect.is_active ? 'Désactiver' : 'Activer'}
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

export default AdminSiteEffectsPage;
